/**
 * Hand a finished document to the browser.
 *
 * Shared by every generator: the maps, the incident packet, the checklist.
 *
 * These are documents to look at, not files to keep — a sector map gets
 * checked on screen and reprinted next week — so they open in a tab rather
 * than landing in Downloads. The tab has a save button in it for the times
 * somebody does want the file.
 */

/**
 * A tab claimed at click time, for a document that is still being built.
 *
 * A browser lets a page open a tab in response to a click, and blocks one
 * asked for a few seconds later — and building a packet takes those seconds.
 * So the tab is opened on the click, and the document is put into it when it
 * is ready. Call this synchronously in the handler, before the first `await`.
 */
export function reserveTab(): Window | null {
  const tab = window.open('', '_blank');
  // Something to look at while the generator runs. Without it the tab sits
  // blank and reads as a page that failed to load. Guarded because the tab is
  // the point and the placeholder is not: a browser that will not let us
  // write into it should still get the document.
  try {
    tab?.document.write(
      '<!doctype html><title>Building…</title>'
      + '<body style="margin:0;display:grid;place-items:center;height:100vh;'
      + 'font:14px system-ui,sans-serif;color:#475569">Building the document…</body>',
    );
  } catch { /* no placeholder, still a tab */ }
  return tab;
}

/** Give up a reserved tab: the document it was waiting for is not coming. */
export function releaseTab(tab: Window | null) {
  tab?.close();
}

/**
 * Show a finished document, in the tab reserved for it where there is one.
 *
 * `filename` is what a save from the viewer should be called. Blob URLs carry
 * no name of their own, so it only reaches the download fallback — a viewer
 * saving from the tab gets the browser's own naming.
 */
export function openBlob(blob: Blob, filename: string, tab?: Window | null) {
  const url = URL.createObjectURL(blob);
  const target = tab && !tab.closed ? tab : window.open('', '_blank');
  if (!target) {
    // Popups blocked and no tab was reserved. Downloading is worse than a tab
    // but much better than nothing happening at all.
    downloadBlob(blob, filename);
    URL.revokeObjectURL(url);
    return;
  }
  // `replace`, not `href`: the placeholder should not become a back-button
  // stop between the tab and the document.
  target.location.replace(url);
  // Revoked late: releasing the object URL before the tab has finished
  // loading from it leaves the viewer with a blank page.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** Save a document to disk. The fallback for when a tab cannot be opened. */
export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoked late: Safari has been known to cancel a download whose object URL
  // is released in the same tick as the click.
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}
